<script lang="ts">
export default {
  name: "TreeNode",
};
</script>

<script lang="ts" setup>
import {computed, onMounted, ref, nextTick} from "vue";
import {pubsub} from "@assets/js/imaios-global/imaios-global";
import {
  fetchChildren,
  convertTaxonFromApplication,
  type Taxon,
} from "./helpers/taxonomy-api.helper";
import {useSelectedNodeStore} from "./store/selected-node.store";
import {
  getStorageTree,
  storeFoldedItems,
  storeUnfoldedItems,
  keyExistsDeep,
} from "./helpers/storage.helper";
import {fetchAndSelectItem} from "../treeView-vue3/helpers";
import {MoreIcon} from "@imaios-js/icons/stroke16";
import {LessIcon} from "@imaios-js/icons/stroke16";
import {Draft16StrokeIcon} from "@imaios-js/icons/stroke16";
import tippy, {roundArrow} from "tippy.js";
import "tippy.js/dist/svg-arrow.css";
import {useI18n} from "vue-i18n";

const {t} = useI18n();

const store = useSelectedNodeStore();

const MIN_WIDTH_SKELETON = 100;
const MAX_WIDTH_SKELETON = 161;

const props = defineProps<{
  data: Taxon | null;
  expandedItem: boolean;
  storageKey: string;
  depth: number;
  lineStyle: boolean;
  application: string; /* ['anatomical', 'zoopaedia'] - anatomical contains e-anatomy & vet-anatomy */
  tooltip: boolean | false;
}>();

const treeItemEl = ref(null);
const storageTree = computed(() => getStorageTree(props.storageKey));
const isExpanded = ref<Boolean>(props.expandedItem);
const isParent = computed(() => props.data.childrenCount > 0);
const isLoadingChildren = ref<Boolean>(false);
const children = ref<Taxon[] | null>(props.data.children ?? null);
const selectedItem = computed(() => store.isSelectedNode(props.data.nodePath));
const isInSelectedPath = computed(() => store.isInSelectedPath(props.data.id));

pubsub.subscribe(pubsub, "refreshTree", async (url) => {
  if (props.data.id) {
    await autoExpandedNodeByUrl(url);
  }

  // Auto scroll to center when auto the item
  const el = treeItemEl.value;
  if (el) {
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }

  // keep position content to center
  window.scrollTo({
    top: 200,
    behavior: "smooth",
  });
});

async function toggleNode() {
  isExpanded.value = !isExpanded.value;

  if (isExpanded.value) {
    imaios.pubsub.publish(`ga-${props.application}-unfold-node`, {
      nodeName: props.data.name,
    });
    storeUnfoldedItems(props.storageKey, props.data.nodePath);
    await loadChildren();

    // Auto scroll to center when auto the item
    const el = treeItemEl.value;
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }

    // keep position content to center
    window.scrollTo({
      top: 200,
      behavior: "smooth",
    });
  } else {
    storeFoldedItems(props.storageKey, props.data.nodePath);
  }
}

async function handleClick() {
  if (!selectedItem.value) {
    await fetchAndSelectItem(props.application, props.data.url, false, false);

    imaios.pubsub.publish(`ga-${props.application}-open-node`, {
      nodeName: props.data.name,
    });

    // keep position content to center
    window.scrollTo({
      top: 200,
      behavior: "smooth",
    });
  }
}

const childrenHaveBeenLoaded = ref(false);

async function loadChildren() {
  if (
      isLoadingChildren.value
      || props.data.childrenCount === 0
      || childrenHaveBeenLoaded.value
  ) return;

  isLoadingChildren.value = true;
  try {
    const childrenRaw = await fetchChildren(props.data.childrenUrl);
    children.value = convertTaxonFromApplication(
        props.application,
        childrenRaw
    );
    childrenHaveBeenLoaded.value = true;
  } catch (error) {
    window.NREUM?.noticeError(error);
  } finally {
    isLoadingChildren.value = false;
  }
}

async function autoExpandedNodeByUrl(url) {
  if (!isInSelectedPath.value) return;

  if (selectedItem.value) {
    /* add data in case we go back in history later */
    window.history.replaceState(url, "");
  } else {
    isExpanded.value = true;
    await loadChildren();
  }
}

/* only Zoopaedia zone */
const isLastRank =
    props.application === "zoopaedia" && props.data.rank === "subspecies";

function shouldForceUnfold(childToUnfold): boolean {
  if (props.application === "anatomical") return false;

  const childrenWithCases = children.value.filter(
      (child) => child.caseCount > 0
  );
  return (
      childrenWithCases.length === 1 &&
      childToUnfold.caseCount > 0 &&
      childToUnfold.childrenCount > 0
  );
}

/* end zoopaedia zone */

/* only Anatomical Structures zone */
const getDefinitionFromLink = async (hash) => {
  if (props.application === "zoopaedia" || !props.tooltip) return;

  try {
    const res = await fetch(`/api/structure/resume/${hash}`);
    const resume = await res.json();

    return makePreviewTooltip(resume);
  } catch (error) {
    throw new Error(`Broken link in description with hash : ${hash}`);
  }
};

function makePreviewTooltip(definitionContent) {
  const {
    text,
    imageSrc,
    imageAlt = "",
    imageBackground = "ffffff",
    descriptionAvailableLang,
    currentLang,
  } = definitionContent;

  const tooltip = document.createElement("div");
  tooltip.classList.add("preview-container");
  tooltip.style.height = "auto";
  tooltip.style.maxHeight = "220px";

  // Div for text or message
  const textDiv = document.createElement("div");
  textDiv.classList.add("preview-text");
  // Element for image
  const imageEl = document.createElement("img");
  imageEl.classList.add("preview-img");
  imageEl.style.width = "220px";
  imageEl.style.height = "220px";

  // Determine message
  const hasText = !!text;
  const hasImage = !!imageSrc;
  const availableInOtherLang = descriptionAvailableLang.length > 0;
  if (hasText) {
    textDiv.innerHTML = text;
  } else if (!hasText && !hasImage && !availableInOtherLang) {
    textDiv.innerHTML = t("structures.content.empty");
    textDiv.style.fontStyle = "italic";
    tooltip.style.width = "auto";
  } else {
    textDiv.innerHTML =
        descriptionAvailableLang.length > 0
            ? t("structures.description.definition.empty.language", {
              language: currentLang,
            })
            : t("structures.description.definition.empty");
    textDiv.style.fontStyle = "italic";
    tooltip.style.width = "auto";
  }
  tooltip.appendChild(textDiv);

  if (hasImage) {
    imageEl.src = imageSrc;
    imageEl.alt = imageAlt;
    imageEl.style.backgroundColor = `#${imageBackground}`;
    tooltip.appendChild(imageEl);
  } else {
    tooltip.classList.add("text-only");
  }

  return tooltip;
}

async function mountPreview(id) {
  const el = treeItemEl.value;

  if (el) {
    tippy(el, {
      allowHTML: true,
      placement: "right",
      theme: "preview-tooltip",
      maxWidth: 480,
      delay: [300, 0],
      interactive: true,
      offset: [0, 10],
      aria: {
        content: "labelledby",
      },
      appendTo: document.body,
      async onShow(instance) {
        const contentHtml = await getDefinitionFromLink(id);
        if (contentHtml) {
          instance.setContent(contentHtml);
          instance.setProps({
            arrow: roundArrow,
          });
        }
      },
    });
  }
}

/* end Anatomical Structures zone */

onMounted(() => {
  nextTick(async () => {
    //wait until child id has been mounted
    if (props.data.id) {
      await mountPreview(props.data.id);
      await autoExpandedNodeByUrl(props.data.url);
    }

    if (isExpanded.value) {
      await loadChildren();
    }

    const el = treeItemEl.value;

    if (selectedItem.value) {
      // Auto scroll to selected node when reload/navigate to page
      const {scrollX, scrollY} = window;

      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });

        window.scrollTo(scrollX, scrollY); // restore immediately window position
      }
    }
  });
});
</script>

<template>
  <li
      role="treeitem"
      class="tree-item"
      :class="{ withLine: lineStyle }"
      :aria-expanded="isExpanded"
      :aria-selected="selectedItem"
      :style="{ '--indent': depth }"
  >
    <div
        :id="data.id"
        class="tree-item--title"
        :aria-selected="selectedItem"
        :style="{ '--indent': depth }"
    >
      <button
          :id="`tree-item-${data.id}`"
          class="tree-item--title__btn"
          v-if="isParent"
          @click="toggleNode(data)"
          :aria-label="
          isExpanded
            ? `${$t('structures.treeView.open')}`
            : `${$t('structures.treeView.close')}`
        "
          :aria-selected="selectedItem"
          :aria-expanded="isExpanded"
      >
        <LessIcon v-if="isExpanded"/>
        <MoreIcon v-else/>
      </button>
      <div v-else/> <!-- space to align item -->

      <div class="tree-item--title__icon" v-if="data.iconPath">
        <img :src="data.iconPath" :alt="data.id + data.name"/>
      </div>

      <Draft16StrokeIcon v-else/>

      <a
          class="tree-item--title__link"
          :class="{ 'font-family-body': isLastRank }"
          :href="data.url"
          @click.prevent="handleClick(data)"
          :aria-selected="selectedItem"
      >
        <span ref="treeItemEl" class="tree-item--title__link-text">
          {{ data.name }}
        </span>
        <span v-if="application == 'zoopaedia'">({{ data.caseCount }})</span>
      </a>
    </div>

    <ul
        role="group"
        class="tree-item--group"
        v-if="isParent && isExpanded"
        :aria-expanded="isExpanded"
    >
      <template v-if="isLoadingChildren">
        <li class="tree--item" v-for="child in data.childrenCount">
          <div class="tree-item--title" :style="{ '--indent': depth + 1 }">
            <button class="tree-item--title__btn card-item-placeholder"/>
            <p
                class="card-item-placeholder mb-0"
                :style="{
                width: Math.floor(Math.random() * (MAX_WIDTH_SKELETON - MIN_WIDTH_SKELETON)) + MIN_WIDTH_SKELETON + 'px', // random width for item skeleton
                height: '20px',
              }"
            ></p>
          </div>
        </li>
      </template>
      <TreeNode
          v-else
          v-for="child in children"
          :key="child.id"
          :data="child"
          :expanded-item="
          keyExistsDeep(storageTree, child.id) || shouldForceUnfold(child)
        "
          :storage-key="storageKey"
          :depth="props.depth + 1"
          :line-style="lineStyle"
          :application="application"
          :tooltip="tooltip"
      />
    </ul>
  </li>
</template>

<style lang="scss">
@use "/assets/css/theme/helpers/variables";

.tree-item--group {
  li:last-child {
    margin-bottom: unset;
  }
}

.tree-item {
  margin-bottom: unset;

  &.withLine {
    position: relative;

    &::before {
      content: "";
      position: absolute;
      border-left: 1px solid variables.$border-color;
      height: calc(100% - 40px);
      left: calc(8px + (var(--indent, 0) * 32px));
      margin-left: 10px;
      top: 40px;
      z-index: 5;
    }
  }
}

.tree-item--title {
  display: grid;
  grid-template-columns: 20px 20px auto;
  align-content: center;
  align-items: center;
  gap: 0.5rem;
  border-radius: variables.$border-light-radius;
  height: 30px;
  padding: 6px 8px 6px calc(8px + (var(--indent, 0) * 32px));

  &:hover {
    background-color: var(--com-container-bg--hover_1);
  }

  &[aria-selected="true"] {
    background-color: var(--layer-accent-selected-02);

    &:hover {
      background-color: variables.$nav-pills-bg-active-hover;
    }
  }

  &__btn {
    border: 0;
    background-color: var(--com-layer--3);
    width: 20px;
    height: 20px;
    padding: unset;
    border-radius: 50%;

    display: flex;
    align-items: center;
    justify-content: center;

    &[aria-expanded="true"] {
      color: var(--link-secondary);
      background-color: var(--layer-accent-selected-02);
    }

    &[aria-selected="true"] {
      background-color: var(--com-container-bg--selected_2);
    }
  }

  &__icon {
    img {
      width: 20px;
      height: 20px;
      object-fit: cover;
    }
  }

  &__link {
    width: 100%;
    font-family: variables.$font-family-base-bold;
    font-size: 14px;
    line-height: 19px;
    color: var(--com-text--secondary);
    display: flex;
    align-items: center;
    white-space: nowrap;
    gap: 0.5rem;

    &-text {
      color: var(--com-text--title);
    }
  }
}
</style>
